import * as angular from 'angular';
import { AuthService } from 'app/_services/auth.service';
import { UtilService } from 'app/_services/util.service';

type PriceStepInformation = {
    largePriceSteps: number;
    smallPriceSteps: number;
};

type ActivityList = {
    activities: (ActivityDto & { activityId: string })[];
};

export class ActivitiesService {
    public CATEGORY = {
        FCFS: 'FCFS',
        LONG_TERM_AUCTION: 'LONG_TERM_AUCTION',
        SHORT_TERM_AUCTION: 'SHORT_TERM_AUCTION',
        SECONDARY: 'SECONDARY',
        SURRENDER: 'SURRENDER',
        REVERSE_AUCTION: 'REVERSE_AUCTION',
        COMFORT_BID: 'COMFORT_BID',
        STORAGE_BID: 'STORAGE_BID'
    };

    public FILTER_CATEGORY_STORAGE = {
        STORAGE_BID: {
            text: 'Auction',
            categories: [this.CATEGORY.STORAGE_BID]
        }
    };

    public FILTER_CATEGORY = {
        AUCTION: {
            text: 'Auction',
            categories: [
                this.CATEGORY.LONG_TERM_AUCTION,
                this.CATEGORY.SHORT_TERM_AUCTION,
                this.CATEGORY.REVERSE_AUCTION
            ]
        },
        SECONDARY_TRADING: {
            text: 'Secondary Trading',
            categories: [this.CATEGORY.SECONDARY]
        },
        FCFS: {
            text: 'FCFS Bookings',
            categories: [this.CATEGORY.FCFS]
        },
        SURRENDERED: {
            text: 'Surrendered',
            categories: [this.CATEGORY.SURRENDER]
        },
        COMFORT_BIDS: {
            text: 'Comfort Bids',
            categories: [this.CATEGORY.COMFORT_BID]
        }
    };

    public STATE = {
        PROCESSED: 'PROCESSED',
        // TODO remove PUBLISHED, is not a state of ActivityDto - moreover it comes from AuctionDto -> AuctionPhaseType
        PREOPEN: 'PREOPEN',
        PUBLISHED: 'PUBLISHED',
        WAITING: 'WAITING',
        CANCELLED: 'CANCELLED',
        APPROVED: 'APPROVED',
        LOST: 'LOST',
        PARTIALLY_WON: 'PARTIALLY_WON',
        OPEN: 'OPEN',
        FINISHED: 'FINISHED',
        NEW: 'NEW',
        WITHDRAWN: 'WITHDRAWN',
        DELETED: 'DELETED',
        REJECTED: 'REJECTED',
        EXPIRED: 'EXPIRED',
        CLOSED: 'CLOSED',
        FULFILLED: 'FULFILLED',
        IN_PROGRESS: 'IN_PROGRESS',
        COMPLETELY_WON: 'COMPLETELY_WON',
        PENDING: 'PENDING',
        APPROVED_EXIT: 'APPROVED_EXIT',
        APPROVED_ENTRY: 'APPROVED_ENTRY',
        NO_ALLOCATIONS: 'NO_ALLOCATIONS',
        AWAITING_ALLOCATION: 'AWAITING_ALLOCATION',
        ALLOCATIONS_UNCONFIRMED: 'ALLOCATIONS_UNCONFIRMED',
        ALLOCATIONS_ACCEPTED: 'ALLOCATIONS_ACCEPTED',
        ALLOCATIONS_REJECTED: 'ALLOCATIONS_REJECTED',
        NOT_ALLOCATED: 'NOT_ALLOCATED'
    };

    public PROCEDURE_TYPE = {
        CFO: {
            LABEL: 'CFO',
            VALUE: 'CFO',
            NORMALIZED: 'Call for Orders (CFO)'
        },
        FCFS: {
            LABEL: 'FCFS',
            VALUE: 'FCFS',
            NORMALIZED: 'First-Come-First-Served (FCFS)'
        },
        OTC: {
            LABEL: 'OTC',
            VALUE: 'OTC',
            NORMALIZED: 'Over the Counter (OTC)'
        }
    };

    public TRANSACTION_TYPE = {
        ASSIGNMENT: 'ASSIGNMENT',
        TRANSFER_OF_USE: 'TRANSFER_OF_USE'
    };

    public PROPOSAL_TYPE = {
        PROPOSAL_TO_SELL: 'PROPOSAL_TO_SELL',
        PROPOSAL_TO_BUY: 'PROPOSAL_TO_BUY'
    };

    public FILTER_STATES_KEYS = {
        OPEN: 'OPEN',
        CLOSED: 'CLOSED',
        ALL: 'ALL'
    };

    public PERIOD_TYPE = {
        YEAR: 'YEAR',
        QUARTER: 'QUARTER',
        MONTH: 'MONTH',
        DAY: 'DAY',
        WITHINDAY: 'WITHINDAY'
    };

    public FILTER = {
        FCFS: {
            OPEN: [this.STATE.WAITING],
            CLOSED: [this.STATE.PROCESSED, this.STATE.CANCELLED]
        },
        LONG_TERM_AUCTION: {
            OPEN: [this.STATE.IN_PROGRESS],
            CLOSED: [
                this.STATE.LOST,
                this.STATE.PARTIALLY_WON,
                this.STATE.COMPLETELY_WON
            ]
        },
        SHORT_TERM_AUCTION: {
            OPEN: [this.STATE.IN_PROGRESS],
            CLOSED: [
                this.STATE.LOST,
                this.STATE.PARTIALLY_WON,
                this.STATE.COMPLETELY_WON
            ],
            COMPLETELY_WON: [],
            PARTIALLY_WON: [],
            LOST: []
        },
        SECONDARY: {
            OPEN: [this.STATE.OPEN, this.STATE.NEW],
            CLOSED: [
                this.STATE.CLOSED,
                this.STATE.FINISHED,
                this.STATE.CANCELLED,
                this.STATE.WITHDRAWN,
                this.STATE.DELETED,
                this.STATE.REJECTED,
                this.STATE.EXPIRED,
                this.STATE.FULFILLED
            ]
        },
        SURRENDER: {
            OPEN: [],
            CLOSED: [] // All (see constructor)
        },
        REVERSE_AUCTION: {
            OPEN: [this.STATE.IN_PROGRESS],
            CLOSED: [
                this.STATE.LOST,
                this.STATE.PARTIALLY_WON,
                this.STATE.COMPLETELY_WON
            ]
        },
        COMFORT_BID: {
            OPEN: [], // All (see constructor)
            CLOSED: []
        },
        STORAGE_BID: {
            OPEN: [
                this.STATE.NO_ALLOCATIONS,
                this.STATE.ALLOCATIONS_UNCONFIRMED,
                this.STATE.AWAITING_ALLOCATION
            ],
            CLOSED: [
                this.STATE.ALLOCATIONS_ACCEPTED,
                this.STATE.ALLOCATIONS_REJECTED,
                this.STATE.NOT_ALLOCATED
            ]
        }
    };

    constructor(
        private authService: AuthService,
        private utilService: UtilService
    ) {
        this.FILTER.SURRENDER.OPEN = this.utilService.values(this.STATE);
        this.FILTER.COMFORT_BID.OPEN = this.utilService.values(this.STATE);
    }

    public map(activities: ActivityDto[]): ActivityList {
        const result = {
            activities: []
        };

        let i: number = 0;
        for (; i < activities.length; i++) {
            const activity = activities[i];

            (activity as any).uiFrame = this.determineFrame(activity);

            // if category is "STORAGE_BID", we might have the same storageBidId and storageOfferId multiple times
            // for different storageProductId's
            const storageProductId = activity.storageProductBidDto
                ? activity.storageProductBidDto.storageProductId
                : undefined;
            // artificial id as the backend doesn't provide one
            (activity as any).activityId =
                `${activity.category}-${activity.auctionId}-${activity.bidId}-${activity.dealId}-${activity.orderId}-${activity.comfortBidId}-${activity.proposalId}-${activity.tradeId}-${activity.reverseAuctionId}-${activity.surrenderId}-${activity.storageBidId}-${activity.storageOfferId}-${storageProductId}`;

            result.activities.push(activity);
        }

        return result;
    }

    public determineFrame(activity): { [key: string]: any } {
        const frame: any = {};

        frame.id = '{not found}';
        switch (activity.category) {
            case this.CATEGORY.FCFS: {
                frame.id = activity.tradeId ? activity.tradeId : activity.bidId;
                break;
            }
            case this.CATEGORY.SECONDARY: {
                frame.id = activity.proposalId;
                if (activity.myOwnResponse) {
                    frame.id = activity.tradeId;
                }
                break;
            }
            case this.CATEGORY.LONG_TERM_AUCTION:
            case this.CATEGORY.SHORT_TERM_AUCTION: {
                frame.id = activity.auctionId;
                break;
            }
            case this.CATEGORY.COMFORT_BID: {
                frame.id = activity.comfortBidId;
                break;
            }
            case this.CATEGORY.SURRENDER: {
                frame.id = activity.surrenderId;
                break;
            }
            case this.CATEGORY.REVERSE_AUCTION: {
                frame.id = activity.reverseAuctionId;
                break;
            }
        }
        frame.final =
            activity.state === this.STATE.FINISHED ||
            activity.state === this.STATE.CANCELLED ||
            activity.state === this.STATE.LOST ||
            activity.state === this.STATE.PARTIALLY_WON ||
            activity.state === this.STATE.COMPLETELY_WON ||
            activity.state === this.STATE.CLOSED ||
            activity.state === this.STATE.FULFILLED ||
            activity.state === this.STATE.WITHDRAWN ||
            activity.state === this.STATE.DELETED ||
            activity.state === this.STATE.REJECTED ||
            activity.state === this.STATE.EXPIRED ||
            activity.state === this.STATE.PROCESSED ||
            activity.state === this.STATE.PENDING;

        if (
            activity.category === this.CATEGORY.SECONDARY &&
            activity.state === this.STATE.NEW &&
            !activity.myOwnResponse
        ) {
            frame.draft = true;
        } else if (
            activity.category === this.CATEGORY.SECONDARY &&
            activity.state === this.STATE.CLOSED
        ) {
            frame.markerClass = 'marker-info';
            frame.markerIconClass = 'fa-circle-o text-info';
        } else {
            switch (activity.state) {
                case this.STATE.ALLOCATIONS_ACCEPTED:
                case this.STATE.PROCESSED:
                case this.STATE.APPROVED:
                case this.STATE.FULFILLED:
                case this.STATE.COMPLETELY_WON:
                case this.STATE.FINISHED: {
                    //BTCPKP-8227 not completely fulfilled proposals should be handled as expired
                    if (
                        !angular.isUndefined(activity.proposalState) &&
                        activity.proposalState === this.STATE.EXPIRED
                    ) {
                        frame.markerClass = 'marker-danger';
                        frame.markerIconClass = 'fa-times-circle text-danger';
                    } else if (
                        !angular.isUndefined(activity.proposalState) &&
                        activity.proposalState === this.STATE.CLOSED
                    ) {
                        frame.markerClass = 'marker-info';
                        frame.markerIconClass = 'fa-circle-o text-info';
                    } else {
                        frame.markerClass = 'marker-success';
                        frame.markerIconClass = 'fa-check-circle text-success';
                    }
                    break;
                }
                case this.STATE.PARTIALLY_WON: {
                    frame.markerClass = 'marker-warning';
                    frame.markerIconClass = 'fa-minus-circle text-warning';
                    break;
                }
                case this.STATE.CANCELLED:
                case this.STATE.ALLOCATIONS_REJECTED:
                case this.STATE.NOT_ALLOCATED:
                case this.STATE.LOST:
                case this.STATE.REJECTED:
                case this.STATE.CLOSED:
                case this.STATE.EXPIRED:
                case this.STATE.WITHDRAWN:
                case this.STATE.DELETED: {
                    frame.markerClass = 'marker-danger';
                    frame.markerIconClass = 'fa-times-circle text-danger';
                    break;
                }
                case this.STATE.WAITING:
                case this.STATE.NO_ALLOCATIONS:
                case this.STATE.ALLOCATIONS_UNCONFIRMED:
                case this.STATE.AWAITING_ALLOCATION:
                case this.STATE.OPEN:
                case this.STATE.NEW:
                case this.STATE.IN_PROGRESS:
                case this.STATE.PENDING:
                case this.STATE.APPROVED_EXIT:
                case this.STATE.APPROVED_ENTRY: {
                    frame.markerClass = 'marker-info';
                    frame.markerIconClass = 'fa-circle-o text-info';
                    break;
                }
            }
        }

        frame.inProgressButLost = false;
        if (
            activity.category === this.CATEGORY.LONG_TERM_AUCTION &&
            activity.state === this.STATE.IN_PROGRESS &&
            activity.round &&
            activity.bid.round
        ) {
            const currentWindowParts: PriceStepInformation =
                this.getSmallAndLargePriceSteps(activity.round);
            const lastWindowParts: PriceStepInformation =
                this.getSmallAndLargePriceSteps(activity.bid.round);
            frame.inProgressButLost = isInProgressButLost(
                currentWindowParts,
                lastWindowParts
            );
        }

        return frame;
    }

    public categoriesToFilter(filter): any[] {
        const categories = [];

        let i = 0;
        for (; i < filter.length; i++) {
            categories.push.apply(categories, filter[i].categories);
        }

        return categories;
    }

    public mapToActivities(
        comfortBids: ComfortBidDto[],
        createdByFirstName: string,
        createdByLastName: string
    ): ActivityDto[] {
        return comfortBids.map(
            (comfortBid) =>
                ({
                    category: this.CATEGORY.COMFORT_BID as ActivityCategory,
                    networkPoint: comfortBid.networkPoint,
                    capacityCategory0: comfortBid.capacityCategory0,
                    capacityCategory1: comfortBid.capacityCategory1,
                    undiscounted: comfortBid.undiscounted,
                    bid: {
                        minimumQuantity: {
                            value: {
                                value: comfortBid.limitBids[0].min.originalValue
                                    .value,
                                floatingPointNumber: true
                            },
                            unit: comfortBid.limitBids[0].min.originalUnit
                        },
                        maximumQuantity: {
                            value: {
                                value: comfortBid.limitBids[0].max.originalValue
                                    .value,
                                floatingPointNumber: true
                            },
                            unit: comfortBid.limitBids[0].max.originalUnit
                        },
                        price: {
                            value: {
                                value: comfortBid.limitBids[0].highestBid
                                    .originalValue.value,
                                floatingPointNumber: true
                            },
                            unit: comfortBid.limitBids[0].highestBid
                                .originalUnit
                        }
                    },
                    runtime: comfortBid.orderRuntime,
                    createdAt: new Date(),
                    state: this.STATE.OPEN as ActivityState,
                    createdBy: `${createdByFirstName} ${createdByLastName}`,
                    uiFrame: {
                        markerClass: 'marker-info',
                        markerIconClass: 'fa-circle-o  text-info'
                    }
                }) as any
        );
    }

    /**
     * Convert a surrender object to an array containing one activity generated from the provided surrender object.
     * This is useful for avoiding one additional request to the <tt>ActivityResource</tt>.
     *
     * @param {Object} surrender The surrender object which should be the back-end SurrenderCapacityDto
     * @param {Object} networkPoint The surrender object does only provide an id, not the object of the
     *  network point, so it needs to be provided explicitly.
     * @returns {Array} An array of length 1 with the object being the activity generated from the surrender object.
     */
    public convertSurrenderToActivity(
        surrender: SurrenderCapacityDto,
        networkPoint: NetworkPointDto
    ): { [key: string]: any }[] {
        const loggedInUser = this.authService.getLoggedInUser();

        const activity = {
            category: this.CATEGORY.SURRENDER,
            state: this.STATE.APPROVED,
            networkPoint: networkPoint,
            runtime: surrender.interval,
            bid: {
                quantity: surrender.quantity
            },
            createdBy: loggedInUser.firstName + ' ' + loggedInUser.lastName,
            createdAt: new Date()
        };
        return [activity];
    }

    public getSmallAndLargePriceSteps(round: string): PriceStepInformation {
        const splitRound: string[] = round.split('.');
        return {
            largePriceSteps: parseInt(splitRound[0]),
            smallPriceSteps: parseInt(splitRound[1])
        };
    }
}

angular.module('app').service('activitiesService', ActivitiesService);

// example for windows: "0.0" -> "1.0" -> "2.0" -> "1.1" -> "1.2"
// you're not allowed to have a _gap_ (e.g. your last window was "1.0" and the current window is "1.1")
function isInProgressButLost(
    currentWindow: PriceStepInformation,
    lastWindow: PriceStepInformation
) {
    // large and small price step are equal ("2.0" and "2.0")
    if (
        currentWindow.largePriceSteps === lastWindow.largePriceSteps &&
        currentWindow.smallPriceSteps === lastWindow.smallPriceSteps
    )
        return false;

    // one large step behind ("3.0" and "2.0")
    if (
        currentWindow.largePriceSteps - 1 === lastWindow.largePriceSteps &&
        currentWindow.smallPriceSteps === 0 &&
        lastWindow.smallPriceSteps === 0
    )
        return false;

    // first small step behind ("1.1" and "2.0")
    if (
        currentWindow.largePriceSteps + 1 === lastWindow.largePriceSteps &&
        currentWindow.smallPriceSteps === 1 &&
        lastWindow.smallPriceSteps === 0
    )
        return false;

    // other small step behind ("1.2" and "1.1")
    if (
        currentWindow.largePriceSteps === lastWindow.largePriceSteps &&
        currentWindow.smallPriceSteps - 1 === lastWindow.smallPriceSteps
    )
        return false;

    // found gap
    return true;
}
